<template>
  <Modal title="People" :subtitle="meeting.title" size="md" @close="$emit('close')">
    <div class="add">
      <PeoplePicker v-model="adding" :exclude="memberIds" placeholder="Invite by name or email" />
      <button class="btn btn-primary" :disabled="!adding.length || busy" @click="invite">
        <span v-if="busy" class="spinner"></span>
        <template v-else>Invite</template>
      </button>
    </div>

    <div class="list-head">
      <span>On this meeting <b>{{ members.length }}</b></span>
      <span class="hint">Co-hosts can admit people, mute, remove and record.</span>
    </div>

    <div class="list" v-if="loading">
      <div class="row" v-for="i in 4" :key="i">
        <div class="skeleton" style="width: 36px; height: 36px; border-radius: 50%"></div>
        <div class="grow">
          <div class="skeleton" style="width: 40%; height: 12px"></div>
          <div class="skeleton" style="width: 60%; height: 10px; margin-top: 6px"></div>
        </div>
      </div>
    </div>
    <div class="list" v-else>
      <div class="row" v-for="u in members" :key="u.id">
        <Avatar :name="nameOf(u)" :image="u.image" :size="36" />
        <div class="grow">
          <strong>
            {{ nameOf(u) }}
            <span class="badge badge-brand" v-if="u.id === creatorId">Organiser</span>
            <span class="badge" v-else-if="isHost(u)">Co-host</span>
          </strong>
          <small>{{ u.email || u.role }}</small>
        </div>
        <template v-if="u.id !== creatorId">
          <label class="cohost" :title="isHost(u) ? 'Remove as co-host' : 'Make co-host'">
            <span>Co-host</span>
            <span class="switch">
              <input type="checkbox" :checked="isHost(u)" @change="toggleHost(u, $event.target.checked)" />
              <span class="track"></span>
            </span>
          </label>
          <button class="icon-btn" title="Remove from meeting" @click="removing = u">
            <i class="ri-user-unfollow-line"></i>
          </button>
        </template>
      </div>
    </div>

    <ConfirmDialog
      v-if="removing"
      danger
      :title="`Remove ${nameOf(removing)}?`"
      message="They will no longer see this meeting or be able to join it without being let in."
      confirm-text="Remove"
      :on-confirm="remove"
      @close="removing = null"
    />
  </Modal>
</template>

<script>
import Modal from "@/components/ui/Modal.vue";
import Avatar from "@/components/ui/Avatar.vue";
import PeoplePicker from "@/components/ui/PeoplePicker.vue";
import ConfirmDialog from "@/components/ui/ConfirmDialog.vue";
import { live, errorMessage } from "@/api";

// Who is on a meeting, for its organiser: invite more, make co-hosts, remove.
export default {
  name: "ManagePeople",
  components: { Modal, Avatar, PeoplePicker, ConfirmDialog },
  emits: ["close", "changed"],
  props: {
    meeting: { type: Object, required: true },
  },
  data() {
    return { full: null, loading: true, adding: [], busy: false, removing: null };
  },
  computed: {
    creatorId() {
      const c = (this.full || this.meeting).creator;
      return c && typeof c === "object" ? c.id : c;
    },
    members() {
      const users = (this.full && this.full.users) || [];
      // The organiser first, then by name.
      return [...users].filter((u) => typeof u === "object").sort((a, b) =>
        a.id === this.creatorId ? -1 : b.id === this.creatorId ? 1 : this.nameOf(a).localeCompare(this.nameOf(b))
      );
    },
    memberIds() {
      return this.members.map((u) => u.id);
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    nameOf(u) {
      return `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email || "Someone";
    },
    isHost(u) {
      return ((this.full && this.full.hosts) || []).includes(u.id);
    },
    toast(e) {
      this.$toast.error(errorMessage(e), { position: "top" });
    },
    async load() {
      try {
        const response = await live.fetch(this.meeting.live_id);
        this.full = response.data.data.live;
      } catch (e) {
        this.toast(e);
      } finally {
        this.loading = false;
      }
    },
    async invite() {
      this.busy = true;
      try {
        await live.addUsers(this.meeting.live_id, this.adding.map((u) => u.id));
        this.$toast.success(`${this.adding.length === 1 ? "1 person" : this.adding.length + " people"} invited`, { position: "top", duration: 2500 });
        this.adding = [];
        await this.load();
        this.$emit("changed");
      } catch (e) {
        this.toast(e);
      } finally {
        this.busy = false;
      }
    },
    async toggleHost(u, on) {
      try {
        const response = await live.hosts(this.meeting.live_id, [u.id], on);
        this.full = response.data.data.live;
      } catch (e) {
        this.toast(e);
        await this.load();
      }
    },
    async remove() {
      try {
        await live.removeUsers(this.meeting.live_id, [this.removing.id]);
        await this.load();
        this.$emit("changed");
      } catch (e) {
        this.toast(e);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.add {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;

  > :first-child {
    flex: 1;
  }

  .btn {
    height: 44px;
  }
}

.list-head {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  margin: 1.4rem 0 0.5rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--ink-2);

  b {
    margin-left: 0.25rem;
    color: var(--ink-3);
  }

  .hint {
    font-weight: 400;
    font-size: 0.8rem;
    color: var(--ink-3);
  }
}

.list {
  display: flex;
  flex-direction: column;
}

.row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0.25rem;
  border-bottom: 1px solid var(--line);

  &:last-child {
    border-bottom: none;
  }

  .grow {
    flex: 1;
    min-width: 0;
  }

  strong {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.9rem;
    font-weight: 600;
  }

  small {
    display: block;
    font-size: 0.8rem;
    color: var(--ink-3);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.cohost {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.78rem;
  color: var(--ink-3);
  cursor: pointer;

  @media (max-width: 480px) {
    > span:first-child {
      display: none;
    }
  }
}
</style>
